import { Save } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, newIdempotencyKey } from "../api";
import { PageBackLink, PageHeader } from "../components";
import { brazilianPhoneToE164, formatBrazilianPhone, formatBrazilianTaxId, formatEmailInput, isValidBrazilianTaxId } from "../input-masks";
import type { Customer } from "../types";
import { AddressFields } from "../AddressFields";

export function NewCustomerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get("returnTo") ?? "";
  const returnTo = requestedReturnTo.startsWith("/") && !requestedReturnTo.startsWith("//") ? requestedReturnTo : "/clientes";
  const [kind, setKind] = useState<"individual" | "company">("company");
  const [legalName, setLegalName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [customerType, setCustomerType] = useState("Condomínio");
  const [taxId, setTaxId] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState({ postalCode: "", street: "", number: "", complement: "", district: "", city: "Rio de Janeiro", state: "RJ", reference: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError("");
    const normalizedPhone = phone ? brazilianPhoneToE164(phone) : undefined;
    const normalizedWhatsapp = whatsapp ? brazilianPhoneToE164(whatsapp) : undefined;
    if (taxId && !isValidBrazilianTaxId(taxId, kind)) return setError(`${kind === "company" ? "CNPJ" : "CPF"} inválido.`);
    if (phone && !normalizedPhone) return setError("Telefone inválido.");
    if (whatsapp && !normalizedWhatsapp) return setError("WhatsApp inválido.");
    const hasAddress = [address.postalCode, address.street, address.number, address.complement, address.district, address.reference]
      .some((value) => value.trim().length > 0);
    const location = hasAddress ? {
      postalCode: address.postalCode.replace(/\D/g, "") || undefined,
      street: address.street.trim() || undefined,
      number: address.number.trim() || undefined,
      complement: address.complement.trim() || undefined,
      district: address.district.trim() || undefined,
      city: address.city.trim() || undefined,
      state: address.state.trim().toUpperCase() || undefined,
      reference: address.reference.trim() || undefined,
    } : undefined;
    setBusy(true);
    try {
      const response = await api<{ data: Customer; duplicateWarnings: unknown[] }>("/customers", {
        method: "POST", headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({
          kind, legalName, tradeName: tradeName || undefined, taxId: taxId.replace(/\D/g, "") || undefined,
          phone: normalizedPhone, whatsapp: normalizedWhatsapp, email: email || undefined,
          customerType, notes: notes || undefined, location,
        }),
      });
      const destination = returnTo === "/clientes" ? returnTo : `${returnTo}${returnTo.includes("?") ? "&" : "?"}customerId=${response.data.id}`;
      navigate(destination, { replace: true });
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Cadastro não concluído."); }
    finally { setBusy(false); }
  };

  return <>
    <section className="page-intro"><div className="app-container"><PageBackLink to={returnTo} context={returnTo === "/clientes" ? "Clientes" : "Novo orçamento"} /><PageHeader title="Novo cliente" /></div></section>
    <section className="app-section compact"><div className="app-container appointment-form-shell">
      <form className="appointment-form" onSubmit={submit} onChangeCapture={() => { if (error) setError(""); }}>
        <section className="form-section"><div className="form-grid">
          <label>Cadastro<select value={kind} onChange={(event) => { const nextKind = event.target.value as "individual" | "company"; setKind(nextKind); setTaxId((value) => formatBrazilianTaxId(value, nextKind)); }}><option value="company">Pessoa jurídica</option><option value="individual">Pessoa física</option></select></label>
          <label>Tipo<select value={customerType} onChange={(event) => setCustomerType(event.target.value)}><option>Condomínio</option><option>Empresa</option><option>Residencial</option><option>Instituição</option><option>Outro</option></select></label>
          <label className="field-span-2">{kind === "company" ? "Razão social" : "Nome"}<input value={legalName} onChange={(event) => setLegalName(event.target.value)} maxLength={180} /></label>
          {kind === "company" && <label>Nome fantasia<input value={tradeName} onChange={(event) => setTradeName(event.target.value)} maxLength={180} /></label>}
          <label>{kind === "company" ? "CNPJ" : "CPF"}<input inputMode="numeric" value={taxId} onChange={(event) => setTaxId(formatBrazilianTaxId(event.target.value, kind))} /></label>
          <label>Telefone<input inputMode="tel" value={phone} onChange={(event) => setPhone(formatBrazilianPhone(event.target.value))} placeholder="(21) 99999-9999" /></label>
          <label>WhatsApp<input inputMode="tel" value={whatsapp} onChange={(event) => setWhatsapp(formatBrazilianPhone(event.target.value))} placeholder="(21) 99999-9999" /></label>
          <label className="field-span-2">E-mail<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(formatEmailInput(event.target.value))} placeholder="nome@empresa.com" /></label>
          <label className="field-span-2">Observações<textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={4000} /></label>
        </div></section>
        <section className="form-section"><h2>Endereço</h2><div className="form-grid">
          <AddressFields value={address} onChange={(patch) => setAddress((current) => ({ ...current, ...patch }))} />
        </div></section>
        {error && <div className="form-error" role="alert">{error}</div>}
        <footer className="form-actions"><Link className="site-button site-button-outline" to={returnTo}>Cancelar</Link><button className="site-button site-button-navy" disabled={busy}><Save size={17} />{busy ? "Salvando…" : "Salvar cliente"}</button></footer>
      </form>
    </div></section>
  </>;
}
